import { SpectateActions } from "@common/constants";
import { REPORT_TEXT_MAX, ReportCategories, type ReportCategory } from "@common/definitions/reports";
import { PacketType } from "@common/packets/packet";
import { SpectatePacket } from "@common/packets/spectatePacket";
import { Game } from "../game";
import { escapeText } from "../utils/misc";
import { t } from "../utils/translations/phrases";
import { UIManager } from "./uiManager";

/** a category's name in the language picked (only its number is sent: the server keeps the English name) */
const categoryName = (name: ReportCategory): string => {
    switch (name) {
        case "Aimbot": return t("Aimbot");
        case "Wallhack": return t("Wallhack");
        case "Teaming": return t("Teaming");
        case "Other": return t("Other");
        default: return name;
    }
};

/**
 * The report dialog: who's being reported, what for (Aimbot, Wallhack, Teaming, Other) and optional details.
 * Opened from the death screen ("Report killer") or the Report button while spectating. The server adds the
 * context (did they kill you, with what) and the admins review it; enough reports hold a winner's payout.
 */
class ReportDialogClass {
    private _root?: HTMLElement;
    private _target?: { readonly id: number, readonly name: string };
    private _category?: number;
    /** a report sent and not answered yet (0.91: "Reported" is said once the server has taken it, never before) */
    private _pending?: { readonly id: number, readonly timer: number };

    open(id: number, name: string): void {
        if (UIManager.reportedPlayerIDs.get(id)) return; // once per player per game
        this._build();
        // names can carry the kill feed's colouring markup: show plain text
        name = name.replace(/<[^>]*>/gu, "").trim() || t("this player");
        this._target = { id, name };
        this._category = undefined;
        const root = this._root!;
        root.querySelector(".rd-title")!.textContent = t("Report <name>", { name });
        root.querySelectorAll<HTMLButtonElement>("[data-category]").forEach(button => button.classList.remove("on"));
        const text = root.querySelector<HTMLTextAreaElement>("textarea")!;
        text.value = "";
        this._count();
        this._sync();
        root.hidden = false;
    }

    close(): void {
        if (this._root) this._root.hidden = true;
    }

    /** the buttons that report this player (the death screen's, the spectating one's) */
    private _buttons(id: number, text: string, disabled: boolean): void {
        document.querySelectorAll<HTMLButtonElement>(`.go-report[data-player="${id}"]`).forEach(button => {
            button.disabled = disabled;
            button.textContent = text;
        });
    }

    private _send(): void {
        const target = this._target;
        if (!target || this._category === undefined) return;
        // the match has let this player go: the report couldn't reach it
        if (!Game.connected) {
            const note = this._root?.querySelector(".rd-note");
            if (note) note.textContent = t("This match has let you go, so the report can't be sent from here: tell the admins on Discord.");
            return;
        }
        const text = this._root!.querySelector<HTMLTextAreaElement>("textarea")!.value.slice(0, REPORT_TEXT_MAX);
        Game.sendPacket(SpectatePacket.create({
            type: PacketType.Spectate,
            spectateAction: SpectateActions.Report,
            playerID: target.id,
            reportCategory: this._category,
            reportText: text
        }));
        UIManager.reportedPlayerIDs.set(target.id, true);
        this._buttons(target.id, t("Sending…"), true);
        if (this._pending) clearTimeout(this._pending.timer);
        // the server answers every report it takes within 3 s (with its case number, or its own id): none in 8 s, it
        // wasn't taken
        const timer = window.setTimeout(() => {
            if (this._pending?.id !== target.id) return;
            this._pending = undefined;
            UIManager.reportedPlayerIDs.delete(target.id);
            this._buttons(target.id, t("Not sent: try again"), false);
        }, 8_000);
        this._pending = { id: target.id, timer };
        this.close();
    }

    /** the server took the report of this player (UIManager.processReportPacket) */
    answered(playerID: number): void {
        if (this._pending?.id === playerID) {
            clearTimeout(this._pending.timer);
            this._pending = undefined;
        }
        this._buttons(playerID, t("Reported"), true);
    }

    private _count(): void {
        const text = this._root!.querySelector<HTMLTextAreaElement>("textarea")!;
        this._root!.querySelector(".rd-count")!.textContent = `${text.value.length} / ${REPORT_TEXT_MAX}`;
    }

    private _sync(): void {
        this._root!.querySelector<HTMLButtonElement>(".rd-send")!.disabled = this._category === undefined;
    }

    private _build(): void {
        if (this._root) return;
        const root = document.createElement("div");
        root.id = "report-dialog";
        root.hidden = true;
        root.innerHTML = `
            <div class="rd-card" role="dialog" aria-modal="true">
                <div class="rd-title">${t("Report")}</div>
                <div class="rd-label">${t("What did they do?")}</div>
                <div class="rd-categories">${ReportCategories.map((name, i) => `<button type="button" data-category="${i}">${categoryName(name)}</button>`).join("")}</div>
                <div class="rd-label">${t("Details")} <span class="rd-optional">${t("(optional)")}</span></div>
                <textarea maxlength="${REPORT_TEXT_MAX}" rows="4" placeholder="${escapeText(t("What did you see? For example: snapped onto me through smoke, or kept running with another player."))}"></textarea>
                <div class="rd-foot">
                    <span class="rd-count"></span>
                    <span class="rd-actions"><button type="button" class="rd-cancel">${t("Cancel")}</button><button type="button" class="rd-send">${t("Send report")}</button></span>
                </div>
                <div class="rd-note">${t("Reports go to the admins with how the round went. False reports don't count against anyone.")}</div>
            </div>`;
        document.body.appendChild(root);
        this._root = root;
        root.querySelectorAll<HTMLButtonElement>("[data-category]").forEach(button => button.addEventListener("click", () => {
            this._category = Number(button.dataset.category);
            root.querySelectorAll("[data-category]").forEach(other => other.classList.toggle("on", other === button));
            this._sync();
        }));
        const text = root.querySelector<HTMLTextAreaElement>("textarea")!;
        text.addEventListener("input", () => this._count());
        // typing in the box shouldn't move or shoot
        for (const type of ["keydown", "keyup", "keypress"]) text.addEventListener(type, event => event.stopPropagation());
        root.querySelector(".rd-cancel")!.addEventListener("click", () => this.close());
        root.querySelector(".rd-send")!.addEventListener("click", () => this._send());
        root.addEventListener("pointerdown", event => { if (event.target === root) this.close(); });
    }
}

export const ReportDialog = new ReportDialogClass();
